import React, { createContext, useContext, useState } from 'react';
import { loadFirestore } from '../lazyFirebase';

const CartContext = createContext();

export const PACK_1 = { name: '30 Days — Lose upto 6 Kg', sub: '1 Pack • 130G • 65–80 Cups', price: 1199, original: 1700, packets: 1 };
export const PACK_2 = { name: '60 Days — Lose upto 12 Kg', sub: '2 Packs • 260G • 140–160 Cups', price: 2399, original: 3400, packets: 2 };
export const PACK_3 = { name: '90 Days — Lose upto 18 Kg', sub: '3 Packs • 390G • 210–240 Cups', price: 3199, original: 5100, packets: 3 };
export const DELIVERY = 199;

// 60-day (2 packets) aur 90-day (3 packets) packs par delivery bilkul FREE hai.
// Rule: order mein kul 2 ya zyada tea packets hon to delivery 0, warna Rs.199.
export const deliveryForPackets = (packets) => (Number(packets) >= 2 ? 0 : DELIVERY);

// Cart/checkout item se tea packets gin'na — item.sub mein "2 Packs" jaisa text hota hai.
export const packetsOfItem = (item) => {
  const text = String((item && (item.sub || item.label || item.name)) || '');
  const m = text.match(/(\d+)\s*Pack/i);
  return m ? parseInt(m[1], 10) : 1;
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [currentPage, setCurrentPage] = useState('home');
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 3200);
  };

  const addToCart = (qty, label, price, sub) => {
    setCart(prev => {
      const existing = prev.find(i => i.label === label);
      if (existing) return prev.map(i => i.label === label ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { label, qty, price, sub: sub || '' }];
    });
    showToast('✅ Added to cart! 🛒');
  };

  const removeItem = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));
  const changeQty = (idx, delta) => {
    setCart(prev =>
      prev.map((item, i) => i === idx ? { ...item, qty: item.qty + delta } : item)
          .filter(item => item.qty > 0)
    );
  };
  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  // Order ID serial wise: OSF-0003810001, OSF-0003810002, ...
  // The counter lives in Firestore's `counters/orders` document; thanks to the transaction,
  // the ID never repeats even when two orders arrive together. The order and the counter are saved in the same
  // transaction, so a failed order never wastes a number.
  const ORDER_SERIAL_START = 3810000; // pehla order = OSF-0003810001
  const saveOrderToFirebase = async (orderData) => {
    const { db, collection, addDoc, serverTimestamp, doc, runTransaction } = await loadFirestore();
    const makePayload = (extra) => ({
      ...orderData,
      ...extra,
      createdAt: serverTimestamp(),
      status: 'pending',
      feedbackEmailSent: false,
    });

    // 1) Save with the serial ID
    try {
      const counterRef = doc(db, 'counters', 'orders');
      const orderRef = doc(collection(db, 'orders'));
      const serialId = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const next = (snap.exists() ? Number(snap.data().last) : ORDER_SERIAL_START) + 1;
        const newId = 'OSF-' + String(next).padStart(10, '0');
        tx.set(counterRef, { last: next });
        tx.set(orderRef, makePayload({ id: newId }));
        return newId;
      });
      return { firestoreId: orderRef.id, orderId: serialId };
    } catch (serialErr) {
      // Even if the counter fails (e.g. rules not published yet), the order must not be lost
      console.warn('Serial order ID failed, using fallback ID:', serialErr && serialErr.message);
    }

    // 2) Fallback: purani random ID. Ek retry: network ka chhota sa hiccup order ko zaya na kare
    const legacyId = generateOrderId();
    try {
      const docRef = await addDoc(collection(db, 'orders'), makePayload({ id: legacyId }));
      return { firestoreId: docRef.id, orderId: legacyId };
    } catch (firstErr) {
      await new Promise(r => setTimeout(r, 800));
      const docRef = await addDoc(collection(db, 'orders'), makePayload({ id: legacyId }));
      return { firestoreId: docRef.id, orderId: legacyId };
    }
  };

  const getOrdersFromFirebase = async () => {
    const { db, collection, query, orderBy, getDocs } = await loadFirestore();
    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
  };

  const updateOrderStatus = async (firestoreId, status) => {
    const { db, collection, query, where, getDocs, doc, updateDoc } = await loadFirestore();
    await updateDoc(doc(db, 'orders', firestoreId), { status });

    // When order is marked as "delivered", send feedback email
    if (status === 'delivered') {
      try {
        const ordersSnap = await getDocs(
          query(collection(db, 'orders'), where('id', '==', firestoreId))
        );
        // Try by firestoreId directly
        const orderRef = doc(db, 'orders', firestoreId);
        const orderSnap = await getDocs(query(collection(db, 'orders')));
        const orderDoc = orderSnap.docs.find(d => d.id === firestoreId);
        if (orderDoc) {
          const orderData = orderDoc.data();
          await sendDeliveryFeedbackEmail(orderData);
          await updateDoc(orderRef, { feedbackEmailSent: true });
        }
      } catch (e) {
        console.warn('Feedback email error:', e);
      }
    }
  };

  const sendDeliveryFeedbackEmail = async (order) => {
    const email = order.customer?.email || order.userEmail;
    if (!email || email === 'guest' || !email.includes('@')) return;

    const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID  || '';
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_FEEDBACK_TEMPLATE_ID || '';
    const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY  || '';
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.warn('EmailJS feedback template not configured');
      return;
    }

    const baseUrl = window.location.origin;
    const orderId = order.id;

    // Links for each star rating
    const feedbackLinks = {
      star1: `${baseUrl}/?feedback=${orderId}&stars=1`,
      star2: `${baseUrl}/?feedback=${orderId}&stars=2`,
      star3: `${baseUrl}/?feedback=${orderId}&stars=3`,
      star4: `${baseUrl}/?feedback=${orderId}&stars=4`,
      star5: `${baseUrl}/?feedback=${orderId}&stars=5`,
    };

    try {
      await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id:  SERVICE_ID,
          template_id: TEMPLATE_ID,
          user_id:     PUBLIC_KEY,
          template_params: {
            to_email:    email,
            to_name:     order.customer?.name || 'Valued Customer',
            from_name:   'Organic Slim Fit',
            from_email:  'contact@organicslimfit.com',
            order_id:    orderId,
            product_name: order.items?.[0]?.name || 'Organic Slim Fit Tea',
            feedback_link_1: feedbackLinks.star1,
            feedback_link_2: feedbackLinks.star2,
            feedback_link_3: feedbackLinks.star3,
            feedback_link_4: feedbackLinks.star4,
            feedback_link_5: feedbackLinks.star5,
            feedback_link:   feedbackLinks.star5,
            support_wa:  '923111100340',
          }
        })
      });
    } catch (e) {
      console.warn('Delivery feedback email error:', e);
    }
  };

  const deleteOrderFromFirebase = async (firestoreId) => {
    const { db, doc, deleteDoc } = await loadFirestore();
    return deleteDoc(doc(db, 'orders', firestoreId));
  };

  const generateOrderId = () => {
    const ts = Date.now().toString().slice(-6);
    const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return 'OSF-' + ts + rand;
  };

  const getOrders = () => { try { return JSON.parse(localStorage.getItem('osf_orders') || '[]'); } catch { return []; } };
  const saveOrders = (o) => localStorage.setItem('osf_orders', JSON.stringify(o));

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeItem, changeQty, clearCart, cartCount,
      currentPage, setCurrentPage,
      toast, showToast,
      getOrders, saveOrders, generateOrderId,
      saveOrderToFirebase, getOrdersFromFirebase,
      updateOrderStatus, deleteOrderFromFirebase,
      sendDeliveryFeedbackEmail,
      PACK_1, PACK_2, PACK_3, DELIVERY, deliveryForPackets, packetsOfItem,
      packs: { pack1: { ...PACK_1 }, pack2: { ...PACK_2 }, pack3: { ...PACK_3 } },
      delivery: DELIVERY
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
